import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, History, Loader2,
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

export default function PettyCashModal({
  open,
  onOpenChange,
  balance,
  movements,
  loading,
  form,
  setForm,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-600" />
            Caja Chica
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
            <div className="text-sm text-amber-600 font-medium">Saldo Actual</div>
            <div className="text-4xl font-bold text-amber-700 mt-1">
              {formatCurrency(balance)}
            </div>
            <div className="text-xs text-amber-500 mt-1">
              {new Date().toLocaleDateString('es-MX', {
                weekday: 'long', day: 'numeric', month: 'long',
              })}
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`p-3 border-2 rounded-xl flex items-center justify-center gap-2 transition-all ${
                  form.type === 'ingreso'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 text-slate-600 hover:border-emerald-400'
                }`}
                onClick={() => setForm({ ...form, type: 'ingreso' })}
                data-testid="petty-cash-ingreso-btn"
              >
                <ArrowDownCircle className="w-5 h-5" />
                <span className="font-medium">Ingreso</span>
              </button>
              <button
                type="button"
                className={`p-3 border-2 rounded-xl flex items-center justify-center gap-2 transition-all ${
                  form.type === 'egreso'
                    ? 'border-red-600 bg-red-50 text-red-700'
                    : 'border-slate-200 text-slate-600 hover:border-red-400'
                }`}
                onClick={() => setForm({ ...form, type: 'egreso' })}
                data-testid="petty-cash-egreso-btn"
              >
                <ArrowUpCircle className="w-5 h-5" />
                <span className="font-medium">Egreso</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pc-amount">Monto *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="pc-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-8"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="petty-cash-amount"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="pc-concept">Concepto *</Label>
                <Input
                  id="pc-concept"
                  placeholder="Ej: Cambio, Propinas..."
                  className="mt-1"
                  value={form.concept}
                  onChange={(e) => setForm({ ...form, concept: e.target.value })}
                  data-testid="petty-cash-concept"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pc-notes">Notas (opcional)</Label>
              <Input
                id="pc-notes"
                placeholder="Detalles adicionales..."
                className="mt-1"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="petty-cash-notes"
              />
            </div>

            <Button
              type="submit"
              className={`w-full h-12 ${
                form.type === 'ingreso'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              data-testid="petty-cash-submit"
            >
              {form.type === 'ingreso' ? (
                <>
                  <ArrowDownCircle className="w-5 h-5 mr-2" />
                  Registrar Ingreso
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-5 h-5 mr-2" />
                  Registrar Egreso
                </>
              )}
            </Button>
          </form>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-700">Movimientos de Hoy</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : movements.length === 0 ? (
              <div className="text-center py-4 text-slate-400 text-sm">
                No hay movimientos registrados hoy
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {movements.map((mov) => (
                  <div
                    key={mov.id}
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      mov.type === 'ingreso'
                        ? 'bg-emerald-50 border border-emerald-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {mov.type === 'ingreso' ? (
                        <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowUpCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <div className="font-medium text-slate-700">{mov.concept}</div>
                        <div className="text-xs text-slate-500">
                          {mov.user_name} •{' '}
                          {new Date(mov.created_at).toLocaleTimeString('es-MX', {
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`font-bold ${
                        mov.type === 'ingreso' ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {mov.type === 'ingreso' ? '+' : '-'}
                      {formatCurrency(mov.amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
